import React, {
  useCallback,
  useContext,
  useMemo, useState,
} from 'react';
import style from './style';
import i18next from 'i18next';
import { useHistory, useLocation } from 'react-router-dom';
import ROUTES from '../../../../config/Routes';
import {
  archiveThirdParty,
  getContextInstanceThirdParty,
  reactivateThirdParty,
} from '../../../../services/edge/ContexteService';
import { Context } from '../../../../states/Store';
import EADataGrid from '../../../../components/common/datatable/EADataGrid';
import { associatedTiersColumns } from './AssociatedTiersColumns';
import { useQuery } from '../../../../config/Config';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from '../../../../services/common/RolesUtils';
import { FormProvider, useForm } from 'react-hook-form';
import ArchiveDialog from '../../ArchiveDialog';
import EAColorIcon from '../../../../components/common/EAColorIcon';

const FORM_ID = 'archive_tp_form'

export const assignRanks = (thirdparties) => {
  const thirdpartiesMap = new Map();
  thirdparties.forEach((tp) => thirdpartiesMap.set(tp.id, tp));

  function getRank(tp) {
    if (tp.parentId === -1) return 1;
    const parent = thirdpartiesMap.get(tp.parentId);
    if (parent.rank === undefined) {
      parent.rank = getRank(parent);
    }
    return parent.rank + 1;
  }

  thirdpartiesMap.forEach((tp) => {
    tp.rank = getRank(tp);
    if (tp.parentId !== -1) {
      const parent = thirdpartiesMap.get(tp.parentId);
      tp.parentIsArchived = parent?.isArchived || false;
    } else {
      tp.parentIsArchived = false;
    }
  });

  return thirdparties;
};

export const getSubtree = (items, startId) => {
  const idMap = {};
  const parentMap = {};

  items.forEach((item) => {
    idMap[item.id] = item;
    if (!parentMap[item.parentId]) {
      parentMap[item.parentId] = [];
    }
    parentMap[item.parentId].push(item);
  });

  const result = [];

  function collect(node) {
    result.push(node);
    const children = parentMap[node.id] || [];
    children.forEach((child) => collect(child));
  }

  if (startId === -1) {
    items.forEach((item) => {
      if (item.parentId === -1) {
        collect(item);
      }
    });
  } else if (idMap[startId]) {
    collect(idMap[startId]);
  }

  return result;
};

const sortTp = (a, b) => {
  if (a.isArchived !== b.isArchived) {
    return a.isArchived ? 1 : -1;
  }
  return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
};

const AssociatedTiers = ({ context, associatedTP, startPoint = -1 }) => {
  const { classes } = style();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const queryUtil = useQuery();
  const location = useLocation();
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [tpIdToArchive, setTpIdToArchive] = useState(null);
  const parsedAssociatedTP = useMemo(() => {
    return getSubtree(assignRanks(associatedTP), startPoint)
      .filter((tp) => tp.id !== startPoint)
      .sort(sortTp);
  }, [associatedTP,startPoint]);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canAddTiers = hasAnyOfPrivileges(
    PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT
  );

  const methods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      motif: '',
    },
  });

  const addTiers = () => {
    if (canAddTiers) {
      history.push(
        `${ROUTES.CONTEXT_DETAIL_ADD_TP}?instance=${context.id}&start=${startPoint}`
      );
    }
  };

  const fetchAssociatedTP = useCallback(async () => {
    if (context) {
      try {
        await getContextInstanceThirdParty(
          state.account.id,
          context.id,
          dispatch
        );
      } catch (e) {
        console.log(e);
      }
    }
  }, [context, dispatch, state.account.id]);

  const archiveTiers = (tpId) => {
    setTpIdToArchive(tpId);
    setIsArchiveOpen(true);
  };

  const archiveTiersCallback = useCallback(async (data) => {
    await archiveThirdParty(
      state.account.id,
      tpIdToArchive,
      data,
      dispatch
    );
    await fetchAssociatedTP();
  },[tpIdToArchive, state.account.id, fetchAssociatedTP, dispatch])

  const reactivateTp = async (tpId) => {
    await reactivateThirdParty(state.account.id, tpId, dispatch);
    fetchAssociatedTP();
  };

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {parsedAssociatedTP.length && context ? (
          <EADataGrid
            loading={true}
            inputRows={parsedAssociatedTP}
            getRowId={(row) => row.id}
            columns={associatedTiersColumns(
              history,
              location,
              queryUtil,
              archiveTiers,
              reactivateTp,
              context.isArchived,
              hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_CONTEXT)
            )}
            customSx={{
              '& .MuiDataGrid-columnHeader:nth-child(2)': { color: '#7E7E8F' },
              "& .MuiDataGrid-main": { boxShadow: 'none', borderRadius: "0px" },
              '& .MuiDataGrid-columnHeadersInner': {
                borderRadius: 0,
              },
            }}
            pageSize={10}
          />
        ) : (
          <>
            {canAddTiers && (
              <div onClick={() => addTiers()} className={classes.emptyContent}>
                <EAColorIcon icon={'add'} />
                <div className={classes.emptyContentTitle}>
                  {i18next.t('context.addTiers.add')}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <FormProvider {...methods}>
        <ArchiveDialog
          FORM_ID={FORM_ID}
          open={isArchiveOpen}
          onClose={() => setIsArchiveOpen(false)}
          callback={archiveTiersCallback}
          traductions={{
            title: i18next.t('context.popUpTiersArchiveTitle'),
            subTitle: i18next.t('context.popUpTiersArchiveSubTitle'),
            feedback: {
              title: i18next.t('context.contextTiersDeactivatedTitle'),
              message: i18next.t('context.contextTiersDeactivated'),
              tip: i18next.t('context.contextTiersDeactivatedTip')
            }
          }}
        />
      </FormProvider>
    </div>
  );
};

export default AssociatedTiers;
